import type { Building } from "building-types/building/buildingTypes";
import type { Level } from "building-types/levels/levelTypes";
import { PorchKeyMeasuresGeometryType } from "building-types/porches/porchKeyMeasuresTypes";
import type { ExteriorMaterialOverrideWithKeyMeasures } from "building-types/porches/porchKeyMeasuresTypes";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { StructureSettings } from "building-types/settings/structureSettings";
import { PorchViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import uid from "common/utils/uid";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { DIST_EPSILON } from "geom/core";
import { extrudePolyLine2InZ, extrudeSquare } from "geom/extrude";
import type GeomTagGroup from "geom/geomTagGroup";
import { intersectRay2PolyLine2Array } from "geom/intersect";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2Copy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep } from "geom/range";
import { makeRectFromLine } from "geom/rect2";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2Lerp } from "geom/vec2";
import { isGroundLevel } from "../level/levelTools";
import { interiorColumnParamsIncludingBreaks } from "./porchColonnade";
import type { DoorProjectionRay } from "./porchEdgeLayout";
import { intersectDoorProjectionRays, placeColumnIfUnsupportedVertexAbove } from "./porchEdgeLayout";
import { exteriorMaterialOverrideWithKeyMeasuresNew, shatterLine2, shatterPolyLine2 } from "./porchUtils";

interface PorchRailingOpts {
  readonly colonnadeOffset: number;
  readonly structureSettings: StructureSettings;
  readonly exteriorSegments: PolyLine2[];
  readonly bottomDatum: number;
  readonly group: GeomTagGroup<ExteriorMaterialOverrideWithKeyMeasures>;
  readonly porchSettings: PorchSettingsBase;
  readonly unsupportedCornerAbovePts: Vec2[];
  readonly doorRays: DoorProjectionRay[];
  readonly level: Level;
  readonly levels: Building["levels"];
  readonly mirror: boolean;
}

export function makePorchRailing({
  colonnadeOffset,
  doorRays,
  structureSettings,
  exteriorSegments,
  bottomDatum,
  group,
  porchSettings,
  unsupportedCornerAbovePts,
  level,
  levels,
  mirror,
}: PorchRailingOpts) {
  const railHeight = 30;
  const balThick = 0.75;
  const balDist = 5;
  const railSillHeight = 4;
  const railThick = 1.5;

  const newExteriorSegments: PolyLine2[] = [];
  for (let i = 0, ic = exteriorSegments.length; i < ic; i++) {
    newExteriorSegments[i] = offsetPolyLine2(exteriorSegments[i], colonnadeOffset);
  }

  if (!newExteriorSegments[0]) {
    return;
  }

  if (porchSettings.frontColonnadeColumnOverride) {
    return;
  }
  const edges = shatterPolyLine2(newExteriorSegments[0]);

  for (const segment of edges) {
    const dist = vec2Dist(segment[0], segment[1], segment[2], segment[3]);

    // project porch doors' rays to outer segment of porch
    const edgeRefTable = porchSettings.doorPathColumnSpread ? intersectDoorProjectionRays(doorRays, segment) : {};

    const unsupportedCornerAboveParams = placeColumnIfUnsupportedVertexAbove(
      structureSettings,
      unsupportedCornerAbovePts,
      segment,
    );

    const interiorColumnParams = interiorColumnParamsIncludingBreaks(
      0,
      edgeRefTable,
      dist,
      porchSettings,
      unsupportedCornerAboveParams,
      false,
      mirror,
    );

    const shatterEdges = shatterLine2(segment, interiorColumnParams);
    const openIndex = Math.floor(interiorColumnParams.length / 2);

    let edgeCount = -1;
    for (const edge of shatterEdges) {
      const porchRailingViewEntityDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.PORCH_RAILING,
        porchSettings.viewNotes?.[PorchViewNoteType.PORCH_RAILING],
      );

      edgeCount++;
      const modEdge = polyLine2Copy(edge);

      // for balconies
      if (!isGroundLevel(level, levels)) {
        const edgeDist = vec2Dist(modEdge[0], modEdge[1], modEdge[2], modEdge[3]); // railings

        const railPl = makeRectFromLine(edge, 2, 2);
        group.add(
          extrudePolyLine2InZ(railPl, bottomDatum + railHeight, railThick),
          exteriorMaterialOverrideWithKeyMeasuresNew(
            "trimExterior0",
            undefined,
            PorchKeyMeasuresGeometryType.NONE,
            porchRailingViewEntityDescriptor,
          ),
        );
        group.add(
          extrudePolyLine2InZ(railPl, bottomDatum + railSillHeight, railThick),
          exteriorMaterialOverrideWithKeyMeasuresNew(
            "trimExterior0",
            undefined,
            PorchKeyMeasuresGeometryType.NONE,
            porchRailingViewEntityDescriptor,
          ),
        );

        const divs = rangeInclusiveByMinStep(0, edgeDist, balDist);
        for (const div of divs) {
          const norm = div / edgeDist;
          const [x, y] = vec2Lerp(modEdge[0], modEdge[1], modEdge[2], modEdge[3], norm);
          group.add(
            extrudeSquare(
              x,
              y,
              bottomDatum + railSillHeight + railThick,
              railHeight - railThick - railSillHeight,
              balThick,
            ),
            exteriorMaterialOverrideWithKeyMeasuresNew(
              "trimExterior0",
              undefined,
              PorchKeyMeasuresGeometryType.NONE,
              porchRailingViewEntityDescriptor,
            ),
          );
        }
      }

      if (porchSettings.doorPathColumnSpread) {
        let hitByDoorRay = false;
        for (const doorRay of doorRays) {
          const {
            ray: { originX, originY, dirX, dirY },
          } = doorRay;
          const intersections = intersectRay2PolyLine2Array([edge], originX, originY, dirX, dirY);
          if (intersections.length === 0 || intersections[0] === undefined) {
            continue;
          }
          hitByDoorRay = true;
        }
        if (hitByDoorRay) {
          continue;
        }
      } else if (interiorColumnParams.length !== 2) {
        if (edgeCount === openIndex) {
          continue;
        }
      } else if (interiorColumnParams.length === 2) {
        continue;
      }

      const edgeDist = vec2Dist(modEdge[0], modEdge[1], modEdge[2], modEdge[3]);

      if (edgeDist < DIST_EPSILON) {
        continue;
      }

      // railings
      const railPl = makeRectFromLine(edge, 2, 2);
      group.add(
        extrudePolyLine2InZ(railPl, bottomDatum + railHeight, railThick),
        exteriorMaterialOverrideWithKeyMeasuresNew(
          "trimExterior0",
          undefined,
          PorchKeyMeasuresGeometryType.NONE,
          porchRailingViewEntityDescriptor,
        ),
      );
      group.add(
        extrudePolyLine2InZ(railPl, bottomDatum + railSillHeight, railThick),
        exteriorMaterialOverrideWithKeyMeasuresNew(
          "trimExterior0",
          undefined,
          PorchKeyMeasuresGeometryType.NONE,
          porchRailingViewEntityDescriptor,
        ),
      );

      const divs = rangeInclusiveByMinStep(0, edgeDist, balDist);
      for (const div of divs) {
        const norm = div / edgeDist;
        const [x, y] = vec2Lerp(modEdge[0], modEdge[1], modEdge[2], modEdge[3], norm);
        group.add(
          extrudeSquare(
            x,
            y,
            bottomDatum + railSillHeight + railThick,
            railHeight - railThick - railSillHeight,
            balThick,
          ),
          exteriorMaterialOverrideWithKeyMeasuresNew(
            "trimExterior0",
            undefined,
            PorchKeyMeasuresGeometryType.NONE,
            porchRailingViewEntityDescriptor,
          ),
        );
      }
    }
  }
}
